import { useState } from "react";
import { Zap } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface Props {
  onSwitchToSignup: () => void;
  onForgotPassword: () => void;
}

export function LoginPage({ onSwitchToSignup, onForgotPassword }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--color-bg-base)" }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg mb-3"
            style={{ background: "var(--color-brand)" }}
          >
            <Zap size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <h1
            className="text-headline font-extrabold tracking-tighter"
            style={{ color: "var(--color-text-primary)" }}
          >
            Stride
          </h1>
          <p className="text-caption mt-1" style={{ color: "var(--color-text-muted)" }}>
            Sign in to your account
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-6"
          style={{
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-default)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-caption font-medium"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                style={{
                  background: "var(--color-bg-raised)",
                  border: "1px solid var(--color-border-default)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-caption font-medium"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-caption transition-colors"
                  style={{ color: "var(--color-accent)" }}
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                style={{
                  background: "var(--color-bg-raised)",
                  border: "1px solid var(--color-border-default)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            {error && (
              <p
                className="text-caption px-3 py-2 rounded-lg"
                style={{
                  color: "#f87171",
                  background: "rgba(248, 113, 113, 0.1)",
                  border: "1px solid rgba(248, 113, 113, 0.2)",
                }}
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-caption font-semibold transition-opacity disabled:opacity-50"
              style={{
                background: "var(--color-accent)",
                color: "#fff",
              }}
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>

        {/* Switch to signup */}
        <p
          className="text-center text-caption mt-4"
          style={{ color: "var(--color-text-muted)" }}
        >
          No account?{" "}
          <button
            onClick={onSwitchToSignup}
            className="font-medium transition-colors"
            style={{ color: "var(--color-accent)" }}
          >
            Sign up
          </button>
        </p>

        {/* Legal links */}
        <div style={{ textAlign: "center", fontSize: "11px", color: "var(--color-text-muted)", marginTop: "16px" }}>
          By signing up, you agree to our{" "}
          <a href="/terms" style={{ color: "var(--color-accent)", textDecoration: "none" }}>Terms</a>
          {" "}and{" "}
          <a href="/privacy" style={{ color: "var(--color-accent)", textDecoration: "none" }}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}
